import React, { useState, useRef, useEffect, useCallback } from 'react';
import './css/DragCounter.css';

const DragCounter = ({ value, setValue, running, setRunning, children }) => {
  const isGreen = running;
  const setIsGreen = setRunning;
  const controlRef = useRef(null);
  const startX = useRef(null);
  const currentX = useRef(null);
  const tapStartX = useRef(null);
  const tapStartTime = useRef(null);
  const intervalRef = useRef(null);
  const isDragging = useRef(false);
  const [highlightState, setHighlightState] = useState(false);

  const mapDeltaXToSpeed = useCallback((deltaX) => {
    const absX = Math.abs(deltaX);
    if (absX < 30) return 1;
    if (absX < 50) return 2;
    if (absX < 70) return 5;
    if (absX < 90) return 10;
    return 30;
  }, []);

  const updateSpeed = useCallback((deltaX) => {
    const direction = deltaX > 0 ? -1 : 1;
    const speed = mapDeltaXToSpeed(deltaX);
    return { speed, direction };
  }, [mapDeltaXToSpeed]);

  const startDrag = useCallback((x) => {
    startX.current = x;
    currentX.current = x;
    isDragging.current = true;
    setHighlightState(true);

    intervalRef.current = setInterval(() => {
      if (startX.current !== null && currentX.current !== null) {
        const deltaX = currentX.current - startX.current;
        const { speed, direction } = updateSpeed(deltaX);
        console.log("Speed: " + speed + " Direction: " + direction);
        setValue((prev) => prev + direction * speed);
      }
    }, 500);
  }, [setValue, updateSpeed]);

  const stopDrag = useCallback(() => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    startX.current = null;
    currentX.current = null;
    isDragging.current = false;
    setHighlightState(false);
  }, []);

  const toggleIndicator = useCallback(() => {
    setIsGreen((prev) => !prev);
  }, [setIsGreen]);

  useEffect(() => {
    const control = controlRef.current;

    const handlePointerDown = (e) => {
      tapStartX.current = e.clientX;
      tapStartTime.current = Date.now();
      isDragging.current = false;

      const handlePointerMove = (e) => {
        const deltaX = e.clientX - tapStartX.current;
        if (!isDragging.current && Math.abs(deltaX) > 10) {
          isDragging.current = true;
          startDrag(e.clientX);
        }
        if (isDragging.current) {
          currentX.current = e.clientX;
        }
      };

      const handlePointerUp = (e) => {
        const deltaX = Math.abs(e.clientX - tapStartX.current);
        const duration = Date.now() - tapStartTime.current;

        const wasDragging = isDragging.current;
        stopDrag();

        if (!wasDragging && deltaX < 10 && duration < 200) {
          toggleIndicator();
        }

        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    };

    control.addEventListener('pointerdown', handlePointerDown);

    return () => {
      control.removeEventListener('pointerdown', handlePointerDown);
      stopDrag();
    };
  }, [toggleIndicator, startDrag, stopDrag]);

  const childWithProps = React.cloneElement(children, { highlight: highlightState });

  return (
    <div
      ref={controlRef}
      className={`control-container ${highlightState ? 'highlighted' : ''}`}
    >
      {childWithProps}
      <div
        id='runningIndicator'
        className={`running-indicator ${isGreen ? 'green' : 'red'}`}
      >
      </div>
    </div>
  )
};

export default DragCounter; 
